import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Star, Clock, Utensils } from 'lucide-react';

export const Chicago: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Best Chinese Food in Chicago | Chinese Food Maps</title>
        <meta name="description" content="Discover the best Chinese restaurants in Chicago. From authentic dim sum in Chinatown to regional specialties in the suburbs, explore Chicago's diverse Chinese food scene." />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 text-red-900">Best Chinese Food in Chicago</h1>

        <div className="prose max-w-none text-gray-600 mb-12">
          <p className="text-lg leading-relaxed">
            Chicago's Chinatown, centered around Wentworth Avenue and Cermak Road, is a vibrant hub of authentic Chinese cuisine. From traditional dim sum houses to specialty regional restaurants, the city offers an impressive array of Chinese culinary experiences, including Cantonese, Sichuan, and Northern Chinese specialties.
          </p>
        </div>

        <h2 className="text-2xl font-bold mb-8 text-black">Top Rated Chinese Food Restaurants</h2>

        <div className="grid gap-8">
          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-semibold text-red-800 mb-6">Chicago Chinatown</h2>
            
            <div className="space-y-8">
              {/* MingHin Cuisine */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">MingHin Cuisine</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.5</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>2168 S Archer Ave, Chicago, IL 60616</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(312) 808-1999</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Hong Kong Style Dim Sum</li>
                    <li>Cantonese BBQ</li>
                    <li>Seafood Specialties</li>
                    <li>Late Night Dining</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Baked BBQ Pork Buns</li>
                    <li>Shrimp Dumplings (Har Gow)</li>
                    <li>Roasted Duck</li>
                    <li>Egg Custard Tarts</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=2168+S+Archer+Ave+Chicago+IL+60616`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:3128081999"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>

              {/* Lao Sze Chuan */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">Lao Sze Chuan</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.4</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>2172 S Archer Ave, Chicago, IL 60616</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(312) 326-5040</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Authentic Sichuan Cuisine</li>
                    <li>Spicy Specialties</li>
                    <li>House-made Chili Oil</li>
                    <li>Tony Hu's Flagship Restaurant</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Three Chili Chicken</li>
                    <li>Dry Chili Fish</li>
                    <li>Mapo Tofu</li>
                    <li>Tony's Special Beef</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=2172+S+Archer+Ave+Chicago+IL+60616`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:3123265040"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>

              {/* Phoenix Restaurant */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">Phoenix Restaurant</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.3</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>2131 S Archer Ave, Chicago, IL 60616</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(312) 328-0848</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Traditional Dim Sum</li>
                    <li>Cart Service</li>
                    <li>Banquet Facilities</li>
                    <li>Cantonese Cuisine</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Siu Mai</li>
                    <li>Chicken Feet</li>
                    <li>Shrimp Rice Noodle Rolls</li>
                    <li>Pan-Fried Turnip Cake</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=2131+S+Archer+Ave+Chicago+IL+60616`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:3123280848"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>

              {/* Triple Crown Restaurant */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">Triple Crown Restaurant</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.2</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>2217 S Wentworth Ave, Chicago, IL 60616</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(312) 842-0088</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Seafood Dishes</li>
                    <li>Late Night Dining</li>
                    <li>Hong Kong Style</li>
                    <li>Family-Style Portions</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Salt & Pepper Squid</li>
                    <li>Peking Duck</li>
                    <li>Lobster with Ginger & Scallion</li>
                    <li>House Special Chow Fun</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=2217+S+Wentworth+Ave+Chicago+IL+60616`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:3128420088"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>

              {/* Dolo Restaurant */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">Dolo Restaurant</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.4</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>2222 S Archer Ave, Chicago, IL 60616</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(312) 674-6688</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Modern Dim Sum</li>
                    <li>Contemporary Cantonese</li>
                    <li>Fresh Seafood</li>
                    <li>Elegant Atmosphere</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Truffle Siu Mai</li>
                    <li>Crispy Roasted Pork Belly</li>
                    <li>Live Seafood Selections</li>
                    <li>Mango Pudding</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=2222+S+Archer+Ave+Chicago+IL+60616`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:3126746688"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-semibold text-red-800 mb-6">Chicago Chinese Food Fun Facts</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Historical Significance</h3>
                  <p className="text-gray-700">Chicago's Chinatown was established in 1912, making it one of the oldest Chinese communities in the Midwest.</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Cultural Heritage</h3>
                  <p className="text-gray-700">The neighborhood preserves traditional Chinese architecture and hosts numerous cultural festivals throughout the year.</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Culinary Evolution</h3>
                  <p className="text-gray-700">Chicago's Chinese food scene has expanded beyond Cantonese to include regional specialties from across China.</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Modern Growth</h3>
                  <p className="text-gray-700">The area continues to grow with new restaurants and businesses, making it a vital part of Chicago's food culture.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-semibold text-red-800 mb-6">Best Times to Visit</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xl font-semibold mb-3">Peak Hours</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <Clock size={18} className="text-red-600" />
                    Dim Sum: 9 AM - 2 PM (weekends)
                  </li>
                  <li className="flex items-center gap-2">
                    <Clock size={18} className="text-red-600" />
                    Dinner: 5 PM - 9 PM
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-3">Special Events</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <Utensils size={18} className="text-red-600" />
                    Lunar New Year Festival (Jan/Feb)
                  </li>
                  <li className="flex items-center gap-2">
                    <Utensils size={18} className="text-red-600" />
                    Summer Fair (July)
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};